/**
 * @module datetime
 *
 * A set of functions for manipulating, formatting, and parsing dates and times.
 *
 * Exports all the functions from `date-fns` + a set of custom functions defined
 * here in this module. This can help encapsulate the date-fns dependency and
 * act as a central location for all date-time related logic.
 *
 * @see https://date-fns.org
 * @see https://github.com/marnusw/date-fns-tz
 * @see https://date-fns.org/v2.23.0/docs/I18n
 * @see https://date-fns.org/v2.23.0/docs/Time-Zones
 */
import {
  formatDistanceStrict as ogFormatDistanceStrict,
  addDays,
  addMinutes,
  addMonths,
  parse,
  parseISO,
  isDate,
  getDay,
  getTime,
  getYear,
  getMonth,
  add,
  sub,
  subDays,
  isBefore,
  isAfter,
  isPast,
  isSameDay,
  isValid,
  isWithinInterval,
  startOfWeek,
  endOfWeek,
  startOfDay,
  startOfMonth,
  endOfMonth,
  startOfYear,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
  subMonths,
  eachDayOfInterval,
} from 'date-fns';
import {
  zonedTimeToUtc,
  utcToZonedTime,
  getTimezoneOffset,
  toDate,
  format as zonedFormat,
  formatInTimeZone as zonedFormatInTimeZone,
} from 'date-fns-tz';
import { enCA, frCA, es } from 'date-fns/locale';
import { defaultLang, supportedLanguages } from '~/config/settings/app.settings';

const LANG_FORMAT_REGEX = /[a-z]{2}-[a-z]{2}/;
const UNSUPPORTED_LANG_ERROR = `Lang not supported. Must be one of ${supportedLanguages.join(', ')}.`;

/**
 * Match app's `lang` strings (e.g., 'en-ca') to date-fns' locale modules.
 *
 * @param {string} lang - locale code (e.g., 'en-ca' or 'es-es')
 * @returns {object} a standard `date-fns` locale object
 *
 * @see https://date-fns.org/v2.23.0/docs/I18n
 */
const findDateLocale = (lang: string) => {
  switch (lang) {
    case 'fr-ca':
      return frCA;
    case 'es-es':
      return es;
    case 'en-ca':
    default:
      return enCA;
  }
};

/**
 * Validate a `lang` string (e.g., is it supported by the app?).
 *
 * @param {string} lang - all-lowercase 4-character locale code (e.g., 'en-ca' or 'es-es')
 * @returns {boolean} is `lang` valid?
 */
const isValidLang = (lang: string) => {
  const isCorrectFormat = LANG_FORMAT_REGEX.test(lang);
  const isSupported = supportedLanguages.includes(lang);

  return isCorrectFormat && isSupported;
};

/**
 * Wrap `format()` with extended timezone function + lang matching.
 *
 * If `timezone` is not provided, it will fallback to the user-agent's value.
 * `timezone` is a string value like 'America/New_York' or 'Europe/Paris'.
 *
 * @param {Date|number} date - the original date
 * @param {string} formatStr - the string of tokens
 * @param {object} options - an object with options
 * @returns {string} the formatted data string
 *
 * @see https://date-fns.org/v2.23.0/docs/format
 * @see https://github.com/marnusw/date-fns-tz#format
 */
const format = (
  date: Date | number,
  formatStr = 'PP',
  options: { lang: string; timeZone?: string } = { lang: defaultLang, timeZone: undefined },
) => {
  const { lang, timeZone, ...builtInOptions } = options;

  if (!isValidLang(lang)) throw new Error(UNSUPPORTED_LANG_ERROR);

  const locale = findDateLocale(lang);

  return zonedFormat(date, formatStr, {
    ...builtInOptions,
    locale,
    timeZone,
  });
};

/**
 * Wrap `formatInTimeZone()` with lang matching.
 *
 * `timezone` is a string value like 'America/New_York' or 'Europe/Paris'.
 *
 * @param {Date|number} date - the original date
 * @param {string} timeZone - the timeZone to format the date in
 * @param {string} formatStr - the string of tokens
 * @param {object} options - an object with options
 * @returns {string} the formatted data string
 *
 * @see https://github.com/marnusw/date-fns-tz#formatInTimeZone
 */
const formatInTimeZone = (
  date: Date | number,
  timeZone: string,
  formatStr = 'PP',
  options: { lang: string } = { lang: defaultLang },
) => {
  const { lang, ...builtInOptions } = options;

  if (!isValidLang(lang)) throw new Error(UNSUPPORTED_LANG_ERROR);

  const locale = findDateLocale(lang);

  const formatOptions = {
    ...builtInOptions,
    locale,
    timeZone,
  };

  return zonedFormatInTimeZone(date, timeZone, formatStr, formatOptions);
};

/**
 * Wrap `formatDistanceStrict()` with lang matching. Return the distance between
 * the given dates in words, using strict units.
 *
 * @param {Date|number} date - the date
 * @param {Date|number} baseDate - the date to compare with
 * @param {object} options - an object with options
 * @returns {string} the distance in words
 *
 * @see https://date-fns.org/v2.23.0/docs/formatDistanceStrict
 */
const formatDistanceStrict = (
  date: Date | number,
  baseDate: Date | number,
  options: { lang: string; unit?: 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year' } = {
    lang: defaultLang,
  },
) => {
  const { lang, ...builtInOptions } = options;

  if (!isValidLang(lang)) throw new Error(UNSUPPORTED_LANG_ERROR);

  const locale = findDateLocale(lang);

  return ogFormatDistanceStrict(date, baseDate, {
    ...builtInOptions,
    locale,
  });
};

/**
 * Transfer the format of the original date string to a format like 'Mar 08, 2222 11:00:00 AM'
 *
 * @param {string} dateInput - the original date string
 * @returns {string} the formatted date string
 */
const fullDateFormat = (dateInput: string) => {
  const date = Date.parse(dateInput) ? new Date(dateInput) : new Date(Number(dateInput));
  return format(date, 'MMM dd, yyyy hh:mm:ss aa');
};

const convertTimezoneTimeToUtcTime = (time: string, timezone: string) => {
  const mockDate = '2023-12-31';
  const date = toDate(`${mockDate}T${time}`, { timeZone: timezone });
  // Splices the time from the date string, like '2023-12-31T11:00:00.000Z' to '11:00:00'
  return date.toISOString().slice(11, 19);
};

const convertUtcTimeToTimezoneTime = (time: string, timezone: string) => {
  const mockDate = '2023-12-31';
  const date = new Date(`${mockDate}T${time}.000Z`);
  return zonedFormatInTimeZone(date, timezone, 'HH:mm:ss');
};

/**
 * @returns {Date} applies UTC offset to the date relative
 * to the timezone of the environment where the code is running
 */
const applyUTCOffset = (date) => {
  let diff = date.getTimezoneOffset();
  date.setMinutes(date.getMinutes() - diff);
};

const DATE_FORMAT_STRING = 'MMM d, yyyy';
const DATE_FORMAT_LABEL_STRING = 'MMM-d-yyyy';

// Input should be the array of Dates, not the array of date string
const sortDates = (dates: Date[]) => {
  return dates.sort((a, b) => a.getTime() - b.getTime());
};

const getTimezoneOffsetString = (timezone: string) => {
  // Based on current time to get target timezone minutes offset, like -300
  const offset = getTimezoneOffset(timezone, new Date()) / 60000;
  if (offset === 0) {
    return 'GMT';
  }
  const sign = offset > 0 ? '+' : '-';
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;

  return `GMT${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const datetime = {
  format,
  formatDistanceStrict,
  formatInTimeZone,
  addDays,
  addMinutes,
  addMonths,
  eachDayOfInterval,
  fullDateFormat,
  findDateLocale,
  isValidLang,
  getDay,
  getTime,
  getYear,
  getMonth,

  parse,
  parseISO,
  isDate,
  add,
  sub,
  subDays,
  isBefore,
  isAfter,
  isPast,
  isSameDay,
  isWithinInterval,
  isValid,

  zonedTimeToUtc,
  utcToZonedTime,
  getTimezoneOffset,
  toDate, // NOTE: don't use original `date-fns/toDate`

  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfDay,
  startOfYear,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
  subMonths,
  convertTimezoneTimeToUtcTime,
  convertUtcTimeToTimezoneTime,

  applyUTCOffset,
  DATE_FORMAT_STRING,
  DATE_FORMAT_LABEL_STRING,
  sortDates,
  getTimezoneOffsetString,
};
